/**
 * Empty state hero for the Recurring transactions page.
 *
 * Shown when the user has zero recurring templates. Uses the
 * reusable ScheduleAnimation component (clock + sliding items)
 * to show the "set it, forget it" metaphor.
 *
 * Follows the Novala standing rule for new-user empty states:
 *   1. Explain the value proposition in one sentence
 *   2. Show a clear primary CTA to add the data
 *   3. Teach how it works (three modes section)
 *
 * The CTA stubs with a toast until PR 11 ships the Builder drawer.
 */

import { useEffect, useState, type CSSProperties } from "react";
import { ScheduleAnimation } from "../../../../components/ScheduleAnimation";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

const PALETTE = {
  ink: "#0E1A1A",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  lineSoft: "#E7EAF0",
};

const TOAST_DURATION_MS = 3200;

const MODES = [
  {
    title: "Scheduled",
    desc: "Fully automatic. Novala posts the transaction on the scheduled date without asking. Best for predictable fixed amounts like rent and subscriptions.",
  },
  {
    title: "Reminder",
    desc: "Creates a reminder in your Vela inbox; you confirm or edit before it posts. Best for amounts that vary like utilities and variable payroll.",
  },
  {
    title: "Unscheduled",
    desc: "A saved template with no schedule. Run it manually from the row actions menu whenever you need. Best for ad-hoc recurring entries.",
  },
];

export function RecurringEmptyState() {
  const { isMobile } = useBreakpoint();
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  const handleCreateFirst = () => {
    setToast("Recurring builder is coming in the next release.");
  };

  return (
    <div style={wrapStyle}>
      <div style={isMobile ? heroStyleMobile : heroStyle}>
        <ScheduleAnimation width={isMobile ? 150 : 190} />
        <h2 style={isMobile ? headingStyleMobile : headingStyle}>
          Automate your scheduled payments
        </h2>
        <p style={isMobile ? descStyleMobile : descStyle}>
          A recurring template fires on the schedule you set: rent on the 1st,
          payroll every other Friday, Shopify on the 25th. Novala posts them
          to the right account automatically, or reminds you to post. Your call.
        </p>
        <button type="button" style={ctaBtnStyle} onClick={handleCreateFirst}>
          Create your first recurring template
        </button>
      </div>

      <div style={isMobile ? howSectionMobile : howSection}>
        <div style={howLabelStyle}>Three modes to choose from</div>
        {MODES.map((mode, idx) => (
          <div key={mode.title} style={isMobile ? howStepMobile : howStep}>
            <div style={isMobile ? stepNumMobile : stepNum}>{idx + 1}</div>
            <div>
              <div style={stepTitleStyle}>{mode.title}</div>
              <div style={stepDescStyle}>{mode.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {toast && <Toast message={toast} />}
    </div>
  );
}

function Toast({ message }: { message: string }) {
  return (
    <div style={toastStyle} role="status" aria-live="polite">
      {message}
    </div>
  );
}

const wrapStyle: CSSProperties = {
  width: "100%",
  background: "#ffffff",
};

const heroStyle: CSSProperties = {
  textAlign: "center",
  padding: "48px 32px 32px",
  maxWidth: 540,
  margin: "0 auto",
};

const heroStyleMobile: CSSProperties = {
  textAlign: "center",
  padding: "28px 16px 20px",
  maxWidth: "100%",
  margin: "0 auto",
};

const headingStyle: CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  margin: "14px 0 10px",
  letterSpacing: "-0.015em",
  color: PALETTE.ink,
};

const headingStyleMobile: CSSProperties = {
  ...headingStyle,
  fontSize: 20,
};

const descStyle: CSSProperties = {
  fontSize: 14,
  color: PALETTE.ink3,
  lineHeight: 1.6,
  margin: "0 auto 22px",
  maxWidth: 440,
};

const descStyleMobile: CSSProperties = {
  ...descStyle,
  fontSize: 12.5,
};

const ctaBtnStyle: CSSProperties = {
  background: PALETTE.ink,
  color: "#ffffff",
  border: 0,
  padding: "11px 22px",
  borderRadius: 10,
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  boxShadow: "0 1px 2px rgba(14, 26, 26, 0.08)",
  fontFamily: "inherit",
};

const howSection: CSSProperties = {
  maxWidth: 640,
  margin: "24px auto 0",
  padding: "32px 24px 56px",
  borderTop: `1px solid ${PALETTE.lineSoft}`,
};

const howSectionMobile: CSSProperties = {
  ...howSection,
  padding: "24px 16px 40px",
};

const howLabelStyle: CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 700,
  color: PALETTE.ink3,
  marginBottom: 18,
  textAlign: "center",
};

const howStep: CSSProperties = {
  display: "flex",
  gap: 18,
  padding: "14px 0",
};

const howStepMobile: CSSProperties = {
  ...howStep,
  padding: "12px 0",
  gap: 14,
};

const stepNum: CSSProperties = {
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: PALETTE.ink,
  color: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 700,
};

const stepNumMobile: CSSProperties = {
  ...stepNum,
  width: 26,
  height: 26,
  fontSize: 12,
};

const stepTitleStyle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  color: PALETTE.ink,
};

const stepDescStyle: CSSProperties = {
  fontSize: 12.5,
  color: PALETTE.ink3,
  marginTop: 3,
  lineHeight: 1.55,
};

const toastStyle: CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  background: PALETTE.ink,
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  boxShadow: "0 8px 20px -4px rgba(14, 26, 26, 0.28)",
  zIndex: 1100,
};